/**
 * Utility functions for handling and parsing error messages from API responses
 */

/**
 * Extracts a clear error message from various error response formats
 * Prioritizes structured error responses from the backend over generic messages
 */
export const extractErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  
  // Check for clear error message in the backend's structured error response
  // Backend returns: { success: false, error: { message: "Clear message", code: "HTTP_400" } }
  if (error?.response?.data?.error?.message) {
    return error.response.data.error.message;
  }
  
  // Fallback to direct message in response data
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Check for error message in the error object itself
  if (error?.message) {
    return error.message;
  }
  
  // Fallback to status-based messages only if no clear message is available
  if (error?.response?.status === 404) return 'The requested resource was not found';
  if (error?.response?.status === 403) return 'You do not have permission to perform this action';
  if (error?.response?.status === 500) return 'An internal server error occurred';
  if (error?.response?.status >= 400) return 'An error occurred while processing your request';
  
  return 'An unexpected error occurred';
};

/**
 * Extracts error details from structured error responses
 */
export const extractErrorDetails = (error: any): any => {
  if (error?.response?.data?.error?.details) {
    return error.response.data.error.details;
  }
  if (error?.response?.data?.details) {
    return error.response.data.details;
  }
  return null;
};

/**
 * Extracts error code from structured error responses
 */
export const extractErrorCode = (error: any): string | null => {
  if (error?.response?.data?.error?.code) {
    return error.response.data.error.code;
  }
  if (error?.response?.data?.code) {
    return error.response.data.code;
  }
  return null;
};

/**
 * Checks if the error is a validation error
 */
export const isValidationError = (error: any): boolean => {
  const message = extractErrorMessage(error);
  const code = extractErrorCode(error);
  return message.toLowerCase().includes('validation') || 
         code?.includes('VALIDATION') || 
         error?.response?.status === 400;
};

/**
 * Checks if the error is a permission/authorization error
 */
export const isPermissionError = (error: any): boolean => {
  const message = extractErrorMessage(error);
  const code = extractErrorCode(error);
  return message.toLowerCase().includes('permission') ||
         message.toLowerCase().includes('access denied') ||
         code?.includes('PERMISSION') ||
         error?.response?.status === 403;
};

/**
 * Creates a user-friendly error message with context
 */
export const createUserFriendlyError = (error: any, context?: string): string => {
  const message = extractErrorMessage(error);
  
  if (context) {
    return `${context}: ${message}`;
  }
  
  return message;
};
